import React from 'react';
import './MessageBubble.css';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';

const MessageBubble = ({ message, onFollowUpClick, followUpsDisabled = false }) => {
  const { text, sender, timestamp, isError } = message;
  // Ensure followUps is always an array
  const followUps = [...new Set((Array.isArray(message.followUps) ? message.followUps : [])
    .filter(question => typeof question === 'string')
    .map(question => question.trim())
    .filter(question => question.length > 0 && question.length <= 500))].slice(0, 5);
  // SageMindLabs: agent artifacts (citations + tool-call trace)
  const citations = Array.isArray(message.citations) ? message.citations.filter(c => c && typeof c === 'object')
    .map(c => ({ heading: typeof c.heading === 'string' ? c.heading : '', text: typeof c.text === 'string' ? c.text : '',
      chunk_id: typeof c.chunk_id === 'string' || typeof c.chunk_id === 'number' ? String(c.chunk_id) : '' })) : [];
  const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls.filter(c => c && typeof c.tool === 'string') : [];
  const usedTools = Array.isArray(message.usedTools) ? message.usedTools.filter(tool => typeof tool === 'string') : [];

  // Format timestamp
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Deduplicate citations by chunk_id
  const uniqueCitations = (() => {
    const seen = new Set();
    const out = [];
    for (const c of citations) {
      const key = c.chunk_id || c.heading || c.text;
      if (key && !seen.has(key)) {
        seen.add(key);
        out.push(c);
      }
    }
    return out;
  })();

  // Timestamp is always shown

  return (
    <div className={`message-bubble ${sender === 'user' ? 'user' : 'bot'} ${isError ? 'error' : ''}`}>
      <div className="message-content">
        <div className="message-avatar">
          {sender === 'user' ? (
            <div className="user-avatar">U</div>
          ) : (
            <div className="bot-avatar">AI</div>
          )}
        </div>
        <div className="message-text-container">
          <div className="message-text">
            <ReactMarkdown rehypePlugins={[rehypeHighlight]}>{text}</ReactMarkdown>
          </div>

          {/* SageMindLabs: tool-call trace (agent transparency) */}
          {sender === 'bot' && toolCalls && Array.isArray(toolCalls) && toolCalls.length > 0 && (
            <div className="agent-tool-trace">
              <details>
                <summary>
                  🛠️ Agent used {toolCalls.length} tool call{toolCalls.length === 1 ? '' : 's'}
                  {usedTools && usedTools.length > 0 ? `: ${usedTools.join(', ')}` : ''}
                </summary>
                <ul className="tool-trace-list">
                  {toolCalls.map((tc, idx) => (
                    <li key={idx}>
                      <span className="tool-name">{tc.tool}</span>
                      <span className="tool-args">{JSON.stringify(tc.args || {})}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}

          {/* SageMindLabs: source citations */}
          {sender === 'bot' && uniqueCitations.length > 0 && (
            <div className="agent-citations">
              <p className="citations-title">📚 Sources:</p>
              <ol className="citations-list">
                {uniqueCitations.map((c, idx) => (
                  <li key={idx} className="citation-item">
                    {c.heading && <span className="citation-heading">{c.heading}</span>}
                    {c.text && <span className="citation-text"> {c.text.slice(0, 160)}{c.text.length > 160 ? '…' : ''}</span>}
                    {c.chunk_id && <span className="citation-chunk"> [source: {c.chunk_id}]</span>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {sender === 'bot' && !isError && followUps.length > 0 && (
            <div className="follow-up-suggestions">
              <p className="follow-up-title">Explore next</p>
              <div className="follow-up-buttons" role="group" aria-label="Suggested follow-up questions">
                {followUps.map(question => (
                  <button
                    key={question}
                    type="button"
                    className="follow-up-button"
                    disabled={followUpsDisabled || typeof onFollowUpClick !== 'function'}
                    onClick={() => onFollowUpClick?.(question)}
                  >
                    <span>{question}</span><span aria-hidden="true">↗</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="message-time">{formatTime(timestamp)}</div>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
